import { createFileRoute, redirect } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import { OutreachDetailPage } from '@apps/web/components/outreaches/outreach_detail_page';
import { useDeepLinkOrganization } from '@apps/web/hooks/use_deep_link_organization';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import {
    createAgentChatDestinationResolver,
    normalizeAgentChatSearch,
} from '@apps/web/lib/agent_chat_navigation';
import { useMode } from '@apps/web/lib/mode_store';
import { claimSlackWebCollaborationDeepLink } from '@apps/web/lib/slack_web_claim_deep_link';

export const outreachDetailsSearchSchema = z.object({
    openChat: z.boolean().optional(),
    chatId: z.string().uuid().optional(),
    chatMode: z.enum(['fullscreen']).optional(),
    slackWebClaim: z.string().optional(),
    contactAttemptId: z.string().optional(),
    outreachAction: z.enum(['assign_and_fill']).optional(),
    organizationId: z.string().optional(),
    mode: z.enum(['live', 'sandbox']).optional(),
});

const resolveAgentChatDestination = createAgentChatDestinationResolver();

export async function handleOutreachDetailBeforeLoad({
    search,
}: {
    readonly search: z.infer<typeof outreachDetailsSearchSchema>;
}): Promise<void> {
    if (
        search.chatId === undefined ||
        search.organizationId === undefined ||
        search.mode === undefined ||
        search.slackWebClaim !== undefined
    ) {
        return;
    }

    const resolution = await resolveAgentChatDestination({
        chatId: search.chatId,
        organizationId: search.organizationId,
        mode: search.mode,
    });
    if (
        resolution.kind === 'stale' ||
        resolution.destination.kind === 'panel'
    ) {
        return;
    }

    const normalizedSearch = normalizeAgentChatSearch(search);
    if (
        normalizedSearch.organizationId === undefined ||
        normalizedSearch.mode === undefined
    ) {
        return;
    }
    throw redirect({
        to: '/agent/runs/$runId',
        params: { runId: resolution.destination.runId },
        search: {
            organizationId: normalizedSearch.organizationId,
            mode: normalizedSearch.mode,
        },
        replace: true,
    });
}

export const Route = createFileRoute('/_app/(main)/outreaches/$callout-id')({
    head: () => ({
        meta: [
            {
                title: 'Open Shift Details | Phoebe',
            },
        ],
    }),
    validateSearch: outreachDetailsSearchSchema,
    beforeLoad: handleOutreachDetailBeforeLoad,
    component: OutreachDetailRouteComponent,
});

export function OutreachDetailRouteComponent() {
    const { 'callout-id': outreachId } = Route.useParams();
    const navigate = Route.useNavigate();
    const {
        openChat,
        chatId,
        chatMode,
        slackWebClaim,
        contactAttemptId,
        outreachAction,
        organizationId: deepLinkOrganizationId,
        mode: deepLinkMode,
    } = Route.useSearch();
    const { isWaitingForDeepLinkOrganization } = useDeepLinkOrganization(
        deepLinkOrganizationId,
        deepLinkMode,
    );
    const organizationId = useOrganizationId();
    const mode = useMode();
    const [resolvedPanelChat, setResolvedPanelChat] = useState<{
        readonly key: string;
        readonly chatId: string;
        readonly chatMode?: 'fullscreen';
        readonly outreachId: string;
    } | null>(null);
    const chatResolutionKey = useMemo(() => {
        if (!chatId) {
            return null;
        }
        return `${chatId}:${deepLinkOrganizationId ?? ''}:${
            slackWebClaim ?? ''
        }`;
    }, [chatId, deepLinkOrganizationId, slackWebClaim]);

    useEffect(() => {
        setResolvedPanelChat(null);
    }, [outreachId]);

    useEffect(() => {
        if (!chatId || organizationId === null || mode === undefined) {
            return;
        }
        if (isWaitingForDeepLinkOrganization || chatResolutionKey === null) {
            return;
        }

        let isCancelled = false;

        void (async () => {
            const canOpenDeepLinkedChat =
                await claimSlackWebCollaborationDeepLink({
                    chatId,
                    organizationId: deepLinkOrganizationId ?? null,
                    slackWebClaim: slackWebClaim ?? null,
                });
            if (!canOpenDeepLinkedChat || isCancelled) {
                return;
            }

            const resolution = await resolveAgentChatDestination({
                chatId,
                organizationId,
                mode,
            });
            if (resolution.kind === 'stale' || isCancelled) {
                return;
            }

            if (resolution.destination.kind === 'run') {
                const normalizedSearch = normalizeAgentChatSearch({
                    organizationId: deepLinkOrganizationId,
                    mode: deepLinkMode,
                });
                void navigate({
                    to: '/agent/runs/$runId',
                    params: { runId: resolution.destination.runId },
                    search: {
                        organizationId:
                            normalizedSearch.organizationId ?? organizationId,
                        mode: normalizedSearch.mode ?? mode,
                    },
                    replace: true,
                });
                return;
            }

            setResolvedPanelChat({
                key: chatResolutionKey,
                chatId: resolution.destination.chatId,
                chatMode,
                outreachId,
            });
            removeResolvedPanelChatParamsFromUrl();
        })();

        return () => {
            isCancelled = true;
        };
    }, [
        chatId,
        chatMode,
        outreachId,
        deepLinkOrganizationId,
        deepLinkMode,
        isWaitingForDeepLinkOrganization,
        chatResolutionKey,
        mode,
        navigate,
        organizationId,
        slackWebClaim,
    ]);

    const resolvedChatIdForCurrentOutreach =
        resolvedPanelChat?.outreachId === outreachId
            ? resolvedPanelChat.chatId
            : undefined;
    const chatIdForDetail = chatId
        ? resolvedPanelChat?.key === chatResolutionKey
            ? resolvedPanelChat.chatId
            : undefined
        : resolvedChatIdForCurrentOutreach;
    const chatModeForDetail = chatIdForDetail
        ? (chatMode ??
          (resolvedPanelChat?.outreachId === outreachId
              ? resolvedPanelChat.chatMode
              : undefined))
        : undefined;
    const [autoOpenChatRequestOutreachId, setAutoOpenChatRequestOutreachId] =
        useState<string | null>(() => (openChat ? outreachId : null));

    useEffect(() => {
        if (openChat) {
            setAutoOpenChatRequestOutreachId(outreachId);
            return;
        }
        setAutoOpenChatRequestOutreachId((currentRequestOutreachId) =>
            currentRequestOutreachId === outreachId
                ? currentRequestOutreachId
                : null,
        );
    }, [outreachId, openChat]);

    useEffect(() => {
        if (!openChat || isWaitingForDeepLinkOrganization) {
            return;
        }

        void navigate({
            search: (previousSearch) => ({
                ...previousSearch,
                openChat: undefined,
            }),
            replace: true,
        });
    }, [isWaitingForDeepLinkOrganization, navigate, openChat]);

    if (isWaitingForDeepLinkOrganization) {
        return null;
    }

    return (
        <OutreachDetailPage
            autoOpenChatOnLoad={autoOpenChatRequestOutreachId === outreachId}
            chatId={chatIdForDetail}
            {...(chatModeForDetail ? { chatMode: chatModeForDetail } : {})}
            outreachAction={outreachAction}
            outreachId={outreachId}
            selectedContactAttemptId={contactAttemptId}
        />
    );
}

function removeResolvedPanelChatParamsFromUrl() {
    const params = new URLSearchParams(window.location.search);
    params.delete('chatId');
    params.delete('agentConversationId');
    params.delete('chatMode');
    params.delete('slackWebClaim');
    const newUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : '');
    window.history.replaceState({}, '', newUrl);
}
